import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
  signal,
  computed,
} from "@angular/core";
import { TaskDataService } from "../../../shared/services/task-data.service";
import { ITaskRequest, TaskRequest } from "../../../modules/tasks/model/types";
import notify from "devextreme/ui/notify";
import validationEngine from "devextreme/ui/validation_engine";
import {
  getNotificationTimeout,
  sendNotificationMessage,
} from "../../../types/resource";
import { LoggingService } from "../../../framework/logging/logging.service";
import { SeverityLevel } from "../../../framework/logging/severity-level.model";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { MessageService } from "../../../shared/components/framework/services/message.service";
import { FormUtils } from "../../../shared/components/framework/services/form-builder.service";
import { LoanDataBase } from "../baseclass/loandata-base";
import {
  IFormSummary,
  IWidgetConfig,
  IFileUploadConfig,
  ILoanAmountGridConfig,
  IDisplayLabelConfig,
  ICurrencyAmountConfig,
} from "../../../framework/models/form-template";
import { DynamicFormBuilderService } from "../../../framework/services/dynamic-form-builder.service";
import { IDynamicFormConfig } from "../../../framework/models/dynamic-form";
import { FeatureFlagsService } from "../../../shared/services/feature-flags.service";
import { FileUploadComponent } from "../../../shared/components/dynamic-form-widgets/file-upload/file-upload.component";

/** Lightweight request-type option derived from IFormSummary. */
interface IDynamicRequestTypeOption {
  /** formType — used as the key to fetch the full template. */
  id: string;
  /** Display name shown in the Request Type drop-down. */
  name: string;
  type: "dynamic";
  referenceType?: string;
}

const AUDIT_CONFIRM_FORM_TYPE = "AUDIT_CONFIRM";

/**
 * ServiceRequestDynamicComponent
 *
 * A self-contained form component that only handles DB-driven dynamic forms.
 * It has no knowledge of the legacy switch-case form configs from
 * ServiceRequestComponent, making it easier to extend and test independently.
 *
 * Usage (inside task-detail or new-task):
 *   <app-service-request-dynamic
 *     [taskTemplateName]="'Servicing Request'"
 *     [task]="existingTask"
 *     (onAddNewTaskEventEmitter)="onTaskCreated($event)">
 *   </app-service-request-dynamic>
 */
@Component({
  standalone: false,
  selector: "app-service-request-dynamic",
  templateUrl: "./service-request-dynamic.component.html",
  styleUrl: "./service-request-dynamic.component.scss",
})
export class ServiceRequestDynamicComponent
  extends LoanDataBase
  implements OnInit
{
  @ViewChild("formElement") formElement!: ElementRef;
  @ViewChild(FileUploadComponent) fileUploadComponent?: FileUploadComponent;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;
  /** When set, only form summaries whose linkedTemplateName matches this value are shown. */
  @Input() linkedTemplateFilter?: string;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  // ── Request types ─────────────────────────────────────────────────────────
  /** Populated from the API on init — only dynamic form templates. */
  requestTypes: IDynamicRequestTypeOption[] = [];

  // ── Form state ────────────────────────────────────────────────────────────
  requestData: any = {};
  files: File[] = [];
  isLoading: boolean = false;
  isEditable: boolean = false;
  today: Date = new Date();

  /** Current DevExtreme form instance — set by (onInitialized) handler. */
  currentFormInstance: any = null;

  /**
   * Guards against the infinite rebuild loop that occurs because the requestType selector now
   * lives inside the dynamic `items` array: rebuilding recreates its dxSelectBox widget, and
   * DevExtreme re-fires onSelectionChanged when that fresh widget initialises with a preset value.
   */
  private lastBuiltRequestTypeId: string | null = null;

  /** Clears the dropdown's search filter so reopening it shows the full list, not just the selected item. */
  onRequestTypeDropdownOpened = (e: any): void => {
    const dataSource = e.component.getDataSource();
    if (dataSource) {
      dataSource.searchValue('');
      dataSource.load();
    }
  };

  /**
   * Whether inactive loans are currently shown.
   * Passed down to app-loan-amount-grid so it can filter lookups.
   */
  override showInactiveLoans: boolean = false;

  // ── Dynamic form signals ──────────────────────────────────────────────────
  readonly dynamicFormConfig = signal<IDynamicFormConfig | null>(null);

  /** Stable array reference for [items] binding — avoids re-creating on every CD cycle. */
  readonly dynamicFormItems = computed<any[]>(
    () => (this.dynamicFormConfig()?.items ?? []) as any[]
  );

  constructor(
    taskDataService: TaskDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private readonly dynamicFormBuilderService: DynamicFormBuilderService,
    featureFlagsService: FeatureFlagsService
  ) {
    super(taskDataService, featureFlagsService);
    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
  }

  // ── File upload ───────────────────────────────────────────────────────────

  onFileUpload(file: File): void {
    this.files.push(file);
    this.updateFileUploadProxy();
  }

  onFileRemove(file: File): void {
    if (file != null && file != undefined) {
      this.files = this.files.filter((f) => f !== file);
    }
    this.updateFileUploadProxy();
  }

  private updateFileUploadProxy(): void {
    const proxyEditor = this.currentFormInstance?.getEditor("fileUploadProxy");
    if (proxyEditor) {
      proxyEditor.option("value", this.files.length.toString());
    }
  }

  // ── Loan amount grid ──────────────────────────────────────────────────────

  onLoanAmountValueChange(fieldKey: string, rows: any[]): void {
    if (!fieldKey) return;

    this.requestData[fieldKey] = rows;
    const proxyEditor =
      this.currentFormInstance?.getEditor("loanAmountsProxy");
    if (proxyEditor) {
      proxyEditor.option("value", rows.length.toString());
    }
    if (fieldKey === 'loanAmounts') {
      this.requestData['bookName'] = rows[0]?.bookName ?? '';
    }
  }

  onCurrencyAmountValueChange(
    amountFieldKey: string,
    value: { amount: number | null; currency: string }
  ): void {
    if (!amountFieldKey) return;

    const config = this.getCurrencyAmountConfig(amountFieldKey);
    const currencyFieldKey = config?.currencyFieldKey ?? 'currency';

    this.requestData[amountFieldKey] = value.amount;
    this.requestData[currencyFieldKey] = value.currency;

    const proxyEditor = this.currentFormInstance?.getEditor(
      this.getCurrencyAmountProxyFieldKey(amountFieldKey)
    );
    if (proxyEditor) {
      proxyEditor.option('value', `${value.currency}|${value.amount ?? ''}`);
    }
  }

  resolveTemplateFieldKey(data: any): string {
    return data?.name ?? data?.dataField ?? data?.fieldKey ?? "";
  }

  getLoanAmountRows(data: any): any[] {
    const fieldKey = this.resolveTemplateFieldKey(data);
    return fieldKey ? (this.requestData[fieldKey] ?? []) : [];
  }

  // ── Form lifecycle ────────────────────────────────────────────────────────

  onFormInitialized(e: any): void {
    this.currentFormInstance = e.component;
  }

  // ── Dynamic widget config helpers ─────────────────────────────────────────

  /**
   * Returns the metadata config for a named custom widget item from the current
   * dynamic form config.  We look this up from the signal rather than relying
   * on DevExtreme passing custom properties through the *dxTemplate context.
   */
  getWidgetConfig(name: string): IWidgetConfig | undefined {
    if (!name) return undefined;
    const items = (this.dynamicFormConfig()?.items ?? []) as any[];
    return this.findItemByName(items, name)?.metadata;
  }

  /** Recursively searches top-level and grouped form items for a matching field/item name. */
  private findItemByName(items: any[], name: string): any | undefined {
    for (const item of items ?? []) {
      if (item?.name === name || item?.dataField === name || item?.fieldKey === name) {
        return item;
      }
      if (Array.isArray(item?.items)) {
        const nested = this.findItemByName(item.items, name);
        if (nested) {
          return nested;
        }
      }
    }
    return undefined;
  }

  /** Recursively traverses top-level and grouped form items. */
  private forEachFormItem(items: any[], visitor: (item: any) => void): void {
    for (const item of items ?? []) {
      visitor(item);
      if (Array.isArray(item?.items)) {
        this.forEachFormItem(item.items, visitor);
      }
    }
  }

  getFileUploadConfig(name: string): IFileUploadConfig | undefined {
    return this.getWidgetConfig(name) as IFileUploadConfig | undefined;
  }

  getLoanAmountGridConfig(name: string): ILoanAmountGridConfig | undefined {
    return this.getWidgetConfig(name) as ILoanAmountGridConfig | undefined;
  }

  getDisplayLabelConfig(name: string): IDisplayLabelConfig | undefined {
    return this.getWidgetConfig(name) as IDisplayLabelConfig | undefined;
  }

  getCurrencyAmountConfig(name: string): ICurrencyAmountConfig | undefined {
    return this.getWidgetConfig(name) as ICurrencyAmountConfig | undefined;
  }

  getCurrencyAmountValue(data: any): { amount: number | null; currency: string } {
    const amountFieldKey = this.resolveTemplateFieldKey(data);
    const config = this.getCurrencyAmountConfig(amountFieldKey);
    const currencyFieldKey = config?.currencyFieldKey ?? 'currency';
    // Region default must win over the template's currencies list order (that list is not region-aware).
    const defaultCurrency = config?.defaultCurrency ?? this.featureFlagsService.getDefaultCurrency() ?? config?.currencies?.[0]?.code;

    return {
      amount: amountFieldKey ? (this.requestData[amountFieldKey] ?? null) : null,
      currency: this.requestData[currencyFieldKey] ?? defaultCurrency
    };
  }

  private getCurrencyAmountProxyFieldKey(amountFieldKey: string): string {
    return `${amountFieldKey}CurrencyAmountProxy`;
  }

  // ── Request type selection ────────────────────────────────────────────────

  async onRequestTypeSelectionChanged(e: any): Promise<void> {
    const selectedItem = e.selectedItem as IDynamicRequestTypeOption;
    if (!selectedItem?.id) return;
    if (selectedItem.id === this.lastBuiltRequestTypeId) return;
    this.lastBuiltRequestTypeId = selectedItem.id;

    const previousRequestType = this.requestData?.requestType;
    const isActualChange =
      !this.isEditable &&
      !!previousRequestType &&
      previousRequestType !== selectedItem.name;

    try {
      this.isLoading = true;

      if (isActualChange) {
        this.resetFormStateForRequestTypeChange();
      }
      // The dropdown's own value binding may not have reached requestData yet at this point.
      this.requestData.requestType = selectedItem.name;

      const template = await firstValueFrom(
        this.taskDataService.getFormTemplate(selectedItem.id)
      );

      const config = this.dynamicFormBuilderService.buildDynamicForm(template);
      config.items = this.replaceRequestTypeItem(config.items as any[], config.form.colCount);

      this.dynamicFormBuilderService.injectFileUploadProxyCallbacks(
        config,
        () => this.files
      );
      this.dynamicFormBuilderService.injectLoanAmountProxyCallbacks(
        config,
        (fieldKey) => this.requestData[fieldKey] ?? []
      );
      this.dynamicFormBuilderService.injectBehaviorCallbacks(
        config,
        this.requestData,
        () => this.currentFormInstance
      );
      this.dynamicFormBuilderService.injectValidationCallbacks(
        config,
        this.requestData
      );

      await firstValueFrom(
        this.dynamicFormBuilderService.resolveDataSources(config)
      );

      // Ensure array fields for loan-amount grids are initialised before the
      // form renders so the grid never receives undefined as its [value] input.
      this.forEachFormItem(config.items as any[], (item: any) => {
        if (
          item?.template === "loanAmountGridTemplate" &&
          item?.name &&
          !Array.isArray(this.requestData[item.name])
        ) {
          this.requestData[item.name] = [];
        }
        if (item?.template === 'currencyAmountTemplate' && item?.name) {
          const widgetConfig = item.metadata as ICurrencyAmountConfig | undefined;
          const currencyFieldKey = widgetConfig?.currencyFieldKey ?? 'currency';
          // Region default must win over the template's currencies list order (that list is not region-aware).
          const defaultCurrency = widgetConfig?.defaultCurrency ?? this.featureFlagsService.getDefaultCurrency() ?? widgetConfig?.currencies?.[0]?.code;
          if (!this.requestData[currencyFieldKey]) {
            this.requestData[currencyFieldKey] = defaultCurrency;
          }
        }
      });

      this.hydrateDateFieldsForEditing(config.items);

      this.dynamicFormConfig.set(config);
    } finally {
      this.isLoading = false;
    }
  }

  /** Clears all user-entered state so no data from the previous request type carries over. */
  private resetFormStateForRequestTypeChange(): void {
    this.requestData = {};
    this.files = [];
    this.fileUploadComponent?.clearAllFiles();
    validationEngine.resetGroup("formGroup");
  }

  /**
   * The dynamic form builder always injects its own "requestType" item (either the backend
   * template's own field, or a readonly default) alongside this component's bound `requestTypes`
   * list, which conflicts with rendering a single, reliably-editable selector. Strip whatever the
   * builder produced — at any nesting depth, since some templates place it inside a section group —
   * and replace it with one authoritative, always-editable item.
   */
  private replaceRequestTypeItem(items: any[], colCount: number): any[] {
    const withoutRequestType = this.removeItemsByDataField(items, "requesttype");
    return [this.buildRequestTypeItem(colCount), ...withoutRequestType];
  }

  /** Recursively strips items (including inside nested `group` items) matching the given dataField. */
  private removeItemsByDataField(items: any[], dataFieldLower: string): any[] {
    return items
      .filter((item) => item?.dataField?.toLowerCase?.() !== dataFieldLower)
      .map((item) =>
        Array.isArray(item?.items)
          ? { ...item, items: this.removeItemsByDataField(item.items, dataFieldLower) }
          : item
      );
  }

  private buildRequestTypeItem(colCount: number): any {
    return {
      dataField: "requestType",
      editorType: "dxSelectBox",
      label: { text: "Request Type" },
      colSpan: colCount,
      cssClass: "dx-widget",
      editorOptions: {
        items: this.requestTypes,
        valueExpr: "name",
        displayExpr: "name",
        required: true,
        searchMode: "contains",
        searchEnabled: true,
        onSelectionChanged: this.onRequestTypeSelectionChanged,
        onOpened: this.onRequestTypeDropdownOpened,
        disabled: this.isEditable,
      },
      validationRules: [
        { type: "required", message: "Request Type is required" },
      ],
    };
  }

  /** Shown before any request type is selected — the sole source for this field thereafter too. */
  private getInitialFormConfig(): IDynamicFormConfig {
    return {
      form: { colCount: 2 },
      items: [this.buildRequestTypeItem(2)],
      dataSources: [],
    };
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async onSubmit(e: any): Promise<void> {
    e.preventDefault();

    const validationResult = validationEngine.validateGroup("formGroup");
    if (!validationResult.isValid) return;

    try {
      const requestType = this.requestTypes.find(
        (t) => t.name === this.requestData.requestType
      );
      if (!requestType) return;

      const request: ITaskRequest = new TaskRequest();
      request.linkedTemplateName = requestType.referenceType;
      request.name = requestType.name;

      const referenceCandidate =
        this.getPrimaryLoanNumber(this.requestData) ||
        this.requestData.dealNumber ||
        "";
      this.requestData.loanNumber = this.requestData.loanNumber || "";

      // Prefer the deal name so EU notifications label this pill "Deal Name" and not a loan number.
      // requestData.dealName is set directly by the dynamic form and is trusted over the loan-number
      // lookup, which silently fails to resolve for EU loans (no dealName/name fields available).
      request.referenceName =
        this.requestData.dealName ||
        this.resolveDealNameForLoanNumber(referenceCandidate) ||
        referenceCandidate ||
        "";

      request.referenceType = requestType.referenceType;

      // Work on a copy so we don't mutate requestData before the form is reset.
      let submissionData = { ...this.requestData };

      const isAuditConfirm =
        requestType.name?.toUpperCase() === "AUDIT CONFIRM" ||
        requestType.id?.toUpperCase() === AUDIT_CONFIRM_FORM_TYPE;

      // Convert selectedDealLoanNumbers display string back to an array for storage.
      if (
        submissionData.selectedDealLoanNumbers &&
        typeof submissionData.selectedDealLoanNumbers === "string"
      ) {
        submissionData.selectedDealLoanNumbers = submissionData.selectedDealLoanNumbers
          .split(",")
          .map((ln: string) => ln.trim())
          .filter((ln: string) => ln.length > 0);
      }

      if (isAuditConfirm && Array.isArray(submissionData.loanAmounts)) {
        submissionData.loanNumbers = submissionData.loanAmounts.map(
          (row: any) => row.loanNumber
        );
        delete submissionData.loanAmounts;
      }

      // Roll up per-row amounts into a top-level total.
      if (!isAuditConfirm && submissionData.loanAmounts) {
        submissionData.amount = submissionData.loanAmounts.reduce(
          (sum: number, la: any) => sum + Number(la.amount || 0),
          0
        );
      }

      submissionData = this.appendDealNameToSubmission(submissionData);

      // Embed the exact "Loan (Deal)" grid text in the JSON blob itself (not a DB column).
      // Underscore-prefixed TaskData keys are already hidden from task-detail display,
      // so this stays invisible to users while still reaching NotificationService.
      submissionData.__fullReferenceName =
        this.requestData.loanAmounts?.[0]?._loanNumberDisplay ||
        request.referenceName ||
        "";

      this.normalizeDateFieldsForSubmission(submissionData);
      this.normalizeErfRequestDataForSubmission(
        submissionData,
        request.linkedTemplateName
      );

      const formData = new FormData();
      formData.append("linkedTemplateName", request.linkedTemplateName ?? "");
      formData.append("locations", request.locations ?? "");
      formData.append("name", request.name);
      formData.append("referenceName", request.referenceName);
      formData.append(
        "requestJsonData",
        this.removeProxyData(JSON.stringify(submissionData))
      );
      formData.append("referenceType", request.referenceType ?? "");

      if (this.isEditable) formData.append("parentId", this.task.parentId);

      this.files.forEach((f) => {
        formData.append("files", f, f.name);
      });

      this.isLoading = true;

      const response = await lastValueFrom(
        this.taskDataService.insertTaskAsFormData(formData)
      );

      sendNotificationMessage(
        `${response[1].data[0].task.taskId} task created`,
        this.messageService
      );

      this.isLoading = false;
      this.formElement.nativeElement.reset();
      this.formElement.nativeElement.submit();
    } catch (err: any) {
      this.isLoading = false;
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, "error", getNotificationTimeout());
    }
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override async ngOnInit(): Promise<void> {
    try {
      this.isLoading = true;

      // Loan/deal lookup data isn't needed to render the Request Type selector itself —
      // start it now so it loads in parallel instead of blocking the dropdown's appearance.
      const loanDataReady = super.ngOnInit();

      // Load only dynamic form templates for this task template name.
      const selectedTemplateType = this.taskTemplateName?.trim().toUpperCase();
      const summariesPromise = firstValueFrom(
        this.taskDataService.getFormTemplates()
      );
      const requestTypeDataPromise = this.linkedTemplateFilter
        ? Promise.resolve(null)
        : lastValueFrom(
            this.taskDataService.getTaskTemplatesByType(this.taskTemplateName)
          );

      const [summaries, requestTypeData] = await Promise.all([
        summariesPromise,
        requestTypeDataPromise,
      ]);

      let filteredSummaries = summaries;

      if (this.linkedTemplateFilter) {
        filteredSummaries = summaries.filter(
          (s: IFormSummary) =>
            s.linkedTemplateName?.trim().toUpperCase() ===
            this.linkedTemplateFilter!.trim().toUpperCase()
        );
      } else {
        const workflowTemplates = requestTypeData?.[1] ?? [];
        const linkedTemplateNames = new Set(
          workflowTemplates
            .map((t: any) => t?.name?.trim().toUpperCase())
            .filter(Boolean)
        );

        filteredSummaries = summaries.filter((s: IFormSummary) =>
          linkedTemplateNames.has(s.linkedTemplateName?.trim().toUpperCase())
        );
      }

      this.requestTypes = filteredSummaries.map((s: IFormSummary) => ({
        id: s.formType,
        name: s.displayName,
        type: "dynamic" as const,
        referenceType: s.linkedTemplateName,
      }));

      this.isEditable = this.task != null;

      if (this.isEditable) {
        // Editing an existing task: the loanAmounts normalisation below reads from
        // loan/deal data, so it must be loaded first.
        await loanDataReady;

        this.requestData = FormUtils.mapFromTask<any>(this.task, function () {
          return {};
        } as any);
        this.normalizeErfRequestDataForEditing(this.requestData);

        if (
          this.requestData.requestType?.toUpperCase() === "AUDIT CONFIRM" &&
          this.requestData.loanNumbers &&
          !this.requestData.loanAmounts
        ) {
          this.requestData.loanAmounts = this.requestData.loanNumbers.map(
            (loanNumber: string) => ({
              key: this.generateUniqueId(),
              loanNumber,
              name: loanNumber,
              amount: 0,
              effectiveDate: new Date(),
            })
          );
        }

        // Normalise loanAmounts rows so the grid has a stable key and all
        // expected fields populated from the loaded loan data.
        if (
          this.requestData.loanAmounts &&
          this.requestData.loanAmounts.length > 0
        ) {
          this.requestData.loanAmounts = this.requestData.loanAmounts.map(
            (amount: any) => ({
              ...amount,
              key: amount.key || this.generateUniqueId(),
              amount: amount.amount,
              name: amount.loanNumber,
              loanNumber: amount.loanNumber,
              dealName:
                this.loanData?.find((d) => d.name === amount.loanNumber)
                  ?.dealName ?? null,
            })
          );
        }

        // Trigger form-config build for the saved request type.
        if (this.requestData.requestType) {
          const requestTypeObj = this.requestTypes.find(
            (rt) => rt.name === this.requestData.requestType
          );
          if (requestTypeObj) {
            await this.onRequestTypeSelectionChanged({
              selectedItem: requestTypeObj,
            });
          }
        }
      } else {
        // Show the selector before the user has picked anything yet — don't wait on
        // loan/deal data, which isn't needed until a request type is actually chosen.
        this.dynamicFormConfig.set(this.getInitialFormConfig());
        await loanDataReady;
      }

      this.isLoading = false;
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, "error", getNotificationTimeout());
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private removeProxyData(data: string): string {
    const json = JSON.parse(data);
    const stripInternalKeys = (value: any): any => {
      if (Array.isArray(value)) {
        return value.map((item) => stripInternalKeys(item));
      }

      if (value && typeof value === 'object') {
        const cleaned: Record<string, any> = {};
        Object.entries(value).forEach(([key, itemValue]) => {
          if (this.isInternalRuntimeKey(key)) {
            return;
          }

          cleaned[key] = stripInternalKeys(itemValue);
        });
        return cleaned;
      }

      return value;
    };

    const cleaned = stripInternalKeys(json);
    delete cleaned["fileUploadProxy"];
    delete cleaned["loanAmountsProxy"];
    return JSON.stringify(cleaned);
  }

  private isInternalRuntimeKey(key: string): boolean {
    // Deliberately sent to the server inside requestJsonData (read by NotificationService),
    // despite the leading underscore that otherwise marks a UI-only proxy field.
    if (key === '__fullReferenceName') return false;
    return typeof key === 'string' && key.startsWith('_');
  }

  private normalizeErfRequestDataForSubmission(
    submissionData: any,
    linkedTemplateName?: string
  ): void {
    if (!this.isErfRequest(linkedTemplateName, submissionData?.requestType)) {
      return;
    }

    this.normalizeErfFacilityDisplayForSubmission(submissionData);

    const rawMargin = Number(submissionData?.reinvestmentMargin);
    if (Number.isNaN(rawMargin)) {
      return;
    }

    submissionData.reinvestmentMargin = rawMargin / 100;
  }

  private normalizeErfFacilityDisplayForSubmission(submissionData: any): void {
    const rawFacilityId = submissionData?.facilityId;
    if (rawFacilityId == null || rawFacilityId === '') {
      return;
    }

    // dxTagBox submits an array (multi-select); dxSelectBox submits a scalar (legacy single-select)
    const isMultiSelect = Array.isArray(rawFacilityId);
    const facilityIds = isMultiSelect ? rawFacilityId : [rawFacilityId];
    if (isMultiSelect && facilityIds.length === 0) {
      return;
    }

    const displayValues = facilityIds
      .map((facilityId: unknown) => this.resolveFacilityDisplayValue(facilityId))
      .filter((display: string | undefined): display is string => !!display);

    if (!displayValues.length) {
      return;
    }

    submissionData.facilityIdDisplay = isMultiSelect ? displayValues : displayValues[0];
  }

  private resolveFacilityDisplayValue(facilityId: unknown): string | undefined {
    const facilityField = this.findFacilityField();
    const valueExpr = facilityField?.editorOptions?.valueExpr ?? 'facilityId';
    const editor = this.currentFormInstance?.getEditor?.('facilityId');

    // Only trust the editor's single 'selectedItem' state when it actually matches this id -
    // required so a dxTagBox multi-select doesn't stamp one facility's label onto every id.
    const selectedItem = editor?.option?.('selectedItem');
    if (selectedItem && String(selectedItem?.[valueExpr]) === String(facilityId)) {
      const selectedDisplay = this.getFacilityDisplayFromItem(
        selectedItem,
        facilityField?.editorOptions?.displayExpr
      );
      if (selectedDisplay) {
        return selectedDisplay;
      }
    }

    const lookupItems = [
      ...(facilityField?._allItems ?? []),
      ...(facilityField?.editorOptions?.items ?? []),
    ];
    const selectedLookupItem = lookupItems.find((item: any) =>
      String(item?.[valueExpr]) === String(facilityId)
    );
    const lookupDisplay = this.getFacilityDisplayFromItem(
      selectedLookupItem,
      facilityField?.editorOptions?.displayExpr
    );
    if (lookupDisplay) {
      return lookupDisplay;
    }

    // Legacy single-value editor fallback, only valid when the editor isn't holding an array
    const editorValue = editor?.option?.('value');
    if (!Array.isArray(editorValue) && String(editorValue) === String(facilityId)) {
      const editorDisplayValue = editor?.option?.('displayValue');
      if (typeof editorDisplayValue === 'string' && editorDisplayValue.trim()) {
        return editorDisplayValue.trim();
      }
    }

    return undefined;
  }

  private findFacilityField(): any | undefined {
    return this.findItemByName((this.dynamicFormConfig()?.items ?? []) as any[], 'facilityId');
  }

  private getFacilityDisplayFromItem(
    item: any,
    displayExpr?: string | ((item: any) => string)
  ): string | undefined {
    if (!item || typeof item !== 'object') {
      return undefined;
    }

    let displayValue: unknown;
    if (typeof displayExpr === 'function') {
      displayValue = displayExpr(item);
    } else if (displayExpr) {
      displayValue = item[displayExpr];
    }

    displayValue ??= item.loanNumberRef ?? item.LoanNumberRef ?? item.name ?? item.displayName;
    return typeof displayValue === 'string' && displayValue.trim()
      ? displayValue.trim()
      : undefined;
  }

  private normalizeErfRequestDataForEditing(editData: any): void {
    if (!this.isErfRequest(this.task?.linkedTemplateName, editData?.requestType)) {
      return;
    }

    const storedMargin = Number(editData?.reinvestmentMargin);
    if (Number.isNaN(storedMargin)) {
      return;
    }

    editData.reinvestmentMargin = storedMargin * 100;
  }

  private isErfRequest(linkedTemplateName?: string, requestType?: string): boolean {
    const normalizedTemplate = linkedTemplateName?.trim().toUpperCase();
    const normalizedRequestType = requestType?.trim().toUpperCase();
    return normalizedTemplate === "ERF REQUEST" || normalizedRequestType === "ERF REQUEST";
  }

  private hydrateDateFieldsForEditing(items: any[]): void {
    this.getDateFieldKeys(items).forEach((fieldKey) => {
      const hydratedValue = this.toEditorDateValue(this.requestData[fieldKey]);

      if (hydratedValue) {
        this.requestData[fieldKey] = hydratedValue;
      }
    });
  }

  private normalizeDateFieldsForSubmission(data: Record<string, any>): void {
    this.getDateFieldKeys().forEach((fieldKey) => {
      const normalizedValue = this.toDateOnlyString(data[fieldKey]);

      if (normalizedValue) {
        data[fieldKey] = normalizedValue;
      }
    });
  }

  private getDateFieldKeys(items: any[] = this.dynamicFormConfig()?.items ?? []): string[] {
    const fieldKeys = new Set<string>();

    const collect = (currentItems: any[] = []): void => {
      currentItems.forEach((item: any) => {
        if (item?.editorType === "dxDateBox" && item?.dataField) {
          fieldKeys.add(item.dataField);
        }

        if (Array.isArray(item?.items) && item.items.length > 0) {
          collect(item.items);
        }
      });
    };

    collect(items);

    return Array.from(fieldKeys);
  }

  private toEditorDateValue(value: unknown): Date | undefined {
    if (value == null || value === "") {
      return undefined;
    }

    if (value instanceof Date) {
      return new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        12,
        0,
        0,
        0
      );
    }

    if (typeof value !== "string") {
      return undefined;
    }

    const datePartsMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (datePartsMatch) {
      const [, year, month, day] = datePartsMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    }

    const parsedValue = new Date(value);
    if (Number.isNaN(parsedValue.getTime())) {
      return undefined;
    }

    return new Date(
      parsedValue.getUTCFullYear(),
      parsedValue.getUTCMonth(),
      parsedValue.getUTCDate(),
      12,
      0,
      0,
      0
    );
  }

  private toDateOnlyString(value: unknown): string | undefined {
    const editorDateValue = this.toEditorDateValue(value);

    if (!editorDateValue) {
      return undefined;
    }

    const year = editorDateValue.getFullYear();
    const month = `${editorDateValue.getMonth() + 1}`.padStart(2, "0");
    const day = `${editorDateValue.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

}